// Вкладка «Наблюдаемость» раздела «Спутники»: когда и где суда молчали.
//
// ⚠️ Раздел ОПИСАТЕЛЬНЫЙ. Он показывает измеренный факт — судно не давало
// позиций дольше собственной нормы, — и НЕ объясняет причину. Гипотезу
// «молчало там, где его слышали» проверили на 6.66 млн точек подтверждённого
// приёма и отвергли: угол места лучшего аппарата в моменты приёма и в моменты
// молчания неразличим (docs/plans/2026-08-02-ring-calibration-nogo.md).
// Поэтому здесь нет ни «подозрительных» провалов, ни вердиктов, а оговорка
// стоит на видном месте, а не в подвале.
//
// Стиль — «Румб» (тема dark/light через токены), графики — инлайновые полоски
// без внешних библиотек: в cabinet-app их нет и ради двух диаграмм не заводим.

import { Fragment, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { cabinetApi, type ObsHour, type ObsScope } from '../api'
import { formatNumber, getLang, t as tAmbient, useLang, useT } from '../i18n'
import { barShare, formatHours, formatMinutes, peakHours, periodPreset } from './obsFormat'
import { ObsMap } from './ObsMap'
import { VesselGapRibbon } from './VesselGapRibbon'

const PERIODS = [
  { days: 7, labelKey: 'obs.period.7d' },
  { days: 30, labelKey: 'obs.period.30d' },
  { days: 90, labelKey: 'obs.period.90d' },
  { days: 365, labelKey: 'obs.period.year' },
]

const SCOPE_KEY: Record<string, string> = {
  own: 'obs.scope.own',
  sitronics: 'obs.scope.sitronics',
  merged: 'obs.scope.merged',
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-hull bg-deep p-3">
      <div className="text-xs uppercase tracking-wide text-mist">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-foam">{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-mist">{hint}</div> : null}
    </div>
  )
}

/** Горизонтальные полоски: ведра длительности провалов. */
function BucketChart({ data }: { data: { bucket: string; count: number }[] }) {
  const max = Math.max(0, ...data.map(d => d.count))
  return (
    <div className="space-y-1.5">
      {data.map(item => (
        <div key={item.bucket} className="flex items-center gap-2 text-xs">
          <span className="w-16 shrink-0 text-right text-mist">{item.bucket}</span>
          <div className="h-3 flex-1 overflow-hidden rounded bg-hull/40">
            <div
              className="h-full rounded bg-sonar/70"
              style={{ width: `${barShare(item.count, max)}%` }}
            />
          </div>
          <span className="w-14 shrink-0 tabular-nums text-foam">
            {item.count.toLocaleString('ru-RU').replace(/\s/g, ' ')}
          </span>
        </div>
      ))}
    </div>
  )
}

/** Профиль суток: в какие часы UTC чаще начинается молчание. */
function HourChart({ data }: { data: ObsHour[] }) {
  const max = Math.max(0, ...data.map(d => d.count))
  const peaks = peakHours(data, 3)
  return (
    <div>
      <div className="flex h-24 items-end gap-[2px]">
        {data.map(item => (
          <div
            key={item.hour}
            title={`${item.hour}:00 UTC — ${item.count}`}
            className={`flex-1 rounded-t ${peaks.includes(item.hour) ? 'bg-sonar' : 'bg-sonar/50'}`}
            style={{ height: `${barShare(item.count, max)}%` }}
          />
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-mist">
        <span>0 UTC</span>
        <span>6</span>
        <span>12</span>
        <span>18</span>
        <span>23</span>
      </div>
    </div>
  )
}

function ProviderRow({ scope, data }: { scope: string; data: ObsScope }) {
  return (
    <tr className="border-t border-hull/60">
      <td className="py-1.5 pr-2 text-foam">
        {SCOPE_KEY[scope] ? tAmbient(SCOPE_KEY[scope]) : scope}
      </td>
      <td className="py-1.5 pr-2 text-right tabular-nums text-mist">
        {formatNumber(getLang(), data.points, 0)}
      </td>
      <td className="py-1.5 pr-2 text-right tabular-nums text-mist">
        {formatNumber(getLang(), data.gaps, 0)}
      </td>
      <td className="py-1.5 pr-2 text-right tabular-nums text-foam">
        {formatHours(data.blind_hours)}
      </td>
      <td className="py-1.5 text-right tabular-nums text-sonar">
        {formatHours(data.blind_hours_closed_by_others)}
      </td>
    </tr>
  )
}

export function ObservabilityPanel() {
  const t = useT()
  const { lang } = useLang()
  const [days, setDays] = useState(30)
  // Раскрытая лента судна: MMSI или null. Одно судно за раз — лента широкая,
  // и две подряд читались бы как одна.
  const [openMmsi, setOpenMmsi] = useState<string | null>(null)
  // Границы периода считаем один раз на выбор: иначе каждый рендер давал бы
  // новый `to` и react-query перезапрашивал бы бесконечно.
  const period = useMemo(() => periodPreset(days), [days])

  const summary = useQuery({
    queryKey: ['cabinet', 'observability', 'summary', period.from, period.to],
    queryFn: () => cabinetApi.observability.summary(period.from, period.to),
  })
  const vessels = useQuery({
    queryKey: ['cabinet', 'observability', 'vessels', period.from, period.to],
    queryFn: () => cabinetApi.observability.vessels(period.from, period.to, 15),
  })
  const gapMap = useQuery({
    queryKey: ['cabinet', 'observability', 'map', period.from, period.to],
    queryFn: () => cabinetApi.observability.map(period.from, period.to),
  })
  const providers = useQuery({
    queryKey: ['cabinet', 'observability', 'providers'],
    queryFn: () => cabinetApi.observability.providers(),
  })

  const data = summary.data

  return (
    <div className="console-scroll h-full overflow-y-auto p-4">
      <div className="mx-auto max-w-5xl space-y-4">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foam">{t('obs.title')}</h2>
            <p className="text-xs text-mist">{t('obs.subtitle')}</p>
          </div>
          <div className="flex gap-1 rounded-lg border border-hull bg-deep p-1">
            {PERIODS.map(p => (
              <button
                key={p.days}
                type="button"
                onClick={() => setDays(p.days)}
                className={`rounded px-2.5 py-1 text-xs ${
                  days === p.days ? 'bg-sonar/20 text-foam' : 'text-mist hover:text-foam'
                }`}
              >
                {t(p.labelKey)}
              </button>
            ))}
          </div>
        </header>

        {/* Оговорка стоит НАД числами: витрина, которую читают как обвинение,
            опаснее отсутствующей. */}
        {data?.disclaimer ? (
          <p className="rounded-lg border border-hull bg-deep p-2.5 text-xs leading-relaxed text-mist">
            {data.disclaimer}
          </p>
        ) : null}

        {summary.isError ? (
          <p className="rounded-lg border border-hull bg-deep p-3 text-sm text-mist">
            {t('obs.summaryFailed')}
          </p>
        ) : null}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Tile
            label={t('obs.gaps')}
            value={summary.isLoading ? '…' : formatNumber(lang, data?.gaps ?? 0, 0)}
            hint={data ? t('obs.acrossVessels', { count: data.vessels }) : undefined}
          />
          <Tile
            label={t('obs.totalSilence')}
            value={summary.isLoading ? '…' : formatHours(data?.blind_hours ?? null)}
          />
          <Tile
            label={t('obs.typicalGap')}
            value={summary.isLoading ? '…' : formatMinutes(data?.median_min ?? null)}
            hint={t('obs.median')}
          />
          <Tile
            label={t('obs.longest')}
            value={summary.isLoading ? '…' : formatMinutes(data?.max_min ?? null)}
          />
        </div>

        {data && data.low_confidence > 0 ? (
          <p className="text-xs text-mist">
            {t('obs.lowConfidence', { count: formatNumber(lang, data.low_confidence, 0) })}
          </p>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-xl border border-hull bg-deep p-3">
            <h3 className="mb-2 text-sm font-medium text-foam">{t('obs.gapDuration')}</h3>
            {data ? <BucketChart data={data.buckets} /> : null}
          </section>

          <section className="rounded-xl border border-hull bg-deep p-3">
            <h3 className="mb-2 text-sm font-medium text-foam">{t('obs.startHour')}</h3>
            {data ? <HourChart data={data.hour_profile} /> : null}
            <p className="mt-2 text-[11px] leading-relaxed text-mist">
              {t('obs.flatProfile')}
            </p>
          </section>
        </div>

        <section className="rounded-xl border border-hull bg-deep p-3">
          <h3 className="mb-2 text-sm font-medium text-foam">{t('obs.whereSilent')}</h3>
          <ObsMap cells={gapMap.data?.cells ?? []} />
          <p className="mt-2 text-[11px] leading-relaxed text-mist">
            {t('obs.cellExplained')}
          </p>
        </section>

        <section className="rounded-xl border border-hull bg-deep p-3">
          <h3 className="mb-2 text-sm font-medium text-foam">{t('obs.secondSubscription')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-mist">
                  <th className="pb-1 font-normal">{t('obs.col.feed')}</th>
                  <th className="pb-1 text-right font-normal">{t('obs.col.points')}</th>
                  <th className="pb-1 text-right font-normal">{t('obs.gaps')}</th>
                  <th className="pb-1 text-right font-normal">{t('obs.col.silence')}</th>
                  <th className="pb-1 text-right font-normal">{t('obs.col.closedByOthers')}</th>
                </tr>
              </thead>
              <tbody>
                {providers.data
                  ? Object.entries(providers.data.scopes)
                      .sort(([a], [b]) => (a === 'merged' ? 1 : b === 'merged' ? -1 : a.localeCompare(b)))
                      .map(([scope, value]) => (
                        <ProviderRow key={scope} scope={scope} data={value} />
                      ))
                  : null}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-mist">
            {t('obs.oneRuler')}
          </p>
        </section>

        <section className="rounded-xl border border-hull bg-deep p-3">
          <h3 className="mb-2 text-sm font-medium text-foam">{t('obs.topVessels')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-mist">
                  <th className="pb-1 font-normal">{t('obs.col.vessel')}</th>
                  <th className="pb-1 text-right font-normal">{t('obs.gaps')}</th>
                  <th className="pb-1 text-right font-normal">{t('obs.col.silence')}</th>
                  <th className="pb-1 text-right font-normal">{t('obs.longest')}</th>
                </tr>
              </thead>
              <tbody>
                {(vessels.data?.vessels ?? []).map(v => (
                  <Fragment key={v.mmsi}>
                    <tr
                      className="cursor-pointer border-t border-hull/60 hover:bg-haze"
                      onClick={() => setOpenMmsi(m => (m === v.mmsi ? null : v.mmsi))}
                      aria-expanded={openMmsi === v.mmsi}
                    >
                      <td className="py-1.5 pr-2 text-foam">
                        <span className="mr-1 inline-block w-2 text-mist">
                          {openMmsi === v.mmsi ? '▾' : '▸'}
                        </span>
                        {v.name}
                        <span className="ml-1.5 text-mist">{v.mmsi}</span>
                      </td>
                      <td className="py-1.5 pr-2 text-right tabular-nums text-mist">{v.gaps}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums text-foam">
                        {formatHours(v.blind_hours)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-mist">
                        {formatMinutes(v.max_min)}
                      </td>
                    </tr>
                    {openMmsi === v.mmsi ? (
                      <tr className="border-t border-hull/40">
                        <td colSpan={4} className="bg-abyss/40">
                          <VesselGapRibbon
                            mmsi={v.mmsi}
                            name={v.name}
                            from={period.from}
                            to={period.to}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-mist">
            {t('obs.ribbonExplained')}
          </p>
        </section>
      </div>
    </div>
  )
}
